// ── MCA Monitor ──────────────────────────────────────────────────────────────
//
// Merchant Cash Advance activity in borrower bank accounts during the holding
// period. Borrowers may not take an MCA while we are lending; this is where a
// portfolio manager sees the evidence and records what they found.
//
// The scan (dbo.usp_ScanMCA) runs nightly with the monitoring sweep, and on
// demand from the Run Scan button. It raises two kinds of finding:
//   NAME     the description matches a company on the MCA list
//            (dbo.MCACompanies). One finding per borrower per company, so the
//            funding deposit and every repayment sit together.
//   PATTERN  recurring fixed-amount debits to a payee that is NOT on the list —
//            daily or weekly, same amount. How an unlisted MCA shows up.
//
// Decisions, all with a note except Acknowledge:
//   Acknowledge     it is an MCA and we know; new activity still collects
//   Cured           paid off / stopped — new activity reopens it
//   False Positive  not an MCA; optionally for every borrower
//
// NOTHING HERE TOUCHES THE BANK DATA. It records a judgement about it.
import { useState, useEffect, Fragment } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, Field, Modal, SearchBar, SectionHeader, Spinner, Td, TdNum, STh } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

const STATUSES = ["Open", "Acknowledged", "Cured", "False Positive", "all"];
const CONF = {
  High:   { bg: "#FBE4DD", fg: "#993C1D" },
  Medium: { bg: "#FDF0D5", fg: "#7A5200" },
  Low:    { bg: "#F1EFE8", fg: "#5F5E5A" },
};
const STATUS_TONE = {
  "Open":           { bg: "#E6F7FC", fg: B.cyanDark },
  "Acknowledged":   { bg: "#FDF0D5", fg: "#7A5200" },
  "Cured":          { bg: "#E1F5EE", fg: "#0F6E56" },
  "False Positive": { bg: "#F1EFE8", fg: "#5F5E5A" },
};
const Pill = ({ tone, children, title }) => (
  <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                               background: tone.bg, color: tone.fg }}>{children}</span>
);
const Tile = ({ label, value, tone, onClick, active, sub }) => (
  <div onClick={onClick}
       style={{ background: active ? "#E6F7FC" : "var(--color-background-secondary)",
                border: "0.5px solid " + (active ? B.cyan : "var(--color-border-tertiary)"),
                borderRadius: 8, padding: "10px 14px", cursor: onClick ? "pointer" : "default", minWidth: 130 }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: tone || "inherit" }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
  </div>
);
const money = v => (v == null || Number(v) === 0) ? "—" : fmt(v);
const when = d => d ? new Date(d).toLocaleString() : "—";

// accountid: embedded on the Account page's Monitoring tab — one borrower,
// every status by default, no book-wide tiles or scan buttons, no Borrower column.
export const MCAMonitorPage = ({ accountid = null } = {}) => {
  const { api, go, role } = useApp();
  const isAdmin = !!(role && role.isAdmin);   // scans are admin-only; the nightly run covers everyone
  const embedded = !!accountid;
  const TableBox = embedded ? "div" : Card;
  const [status, setStatus] = useState(embedded ? "all" : "Open");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});              // id -> true
  const [open, setOpen] = useState(null);          // expanded finding id
  const [txns, setTxns] = useState({});            // id -> rows
  const [decide, setDecide] = useState(null);      // { ids, status }
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState(null);

  const load = (s = status) => {
    setErr(null);
    api.get("getMCAFindings", embedded ? { status: s, accountid } : { status: s })
      .then(d => { setData(d); setSel({}); })
      .catch(e => setErr(e.message));
  };
  useEffect(() => { load(status); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = id => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (!txns[id]) api.get("getMCAFindings", { findingid: id })
      .then(d => setTxns(t => ({ ...t, [id]: d.txns || [] })))
      .catch(() => setTxns(t => ({ ...t, [id]: [] })));
  };

  const runScan = async full => {
    setRunning(true); setRunMsg(null);
    try {
      const r = await api.post("runMCAScan", { full: !!full });
      setRunMsg(`Scanned ${Number(r.txnsscanned || 0).toLocaleString()} transactions · ${r.newfindings || 0} new findings · `
              + `${(r.namehits || 0) + (r.patternhits || 0)} transactions flagged${r.reopened ? ` · ${r.reopened} reopened` : ""}`);
      load();
    } catch (e) { setRunMsg("Scan failed: " + e.message); }
    setRunning(false);
  };

  const rows = (data && data.findings || [])
    .filter(f => !q || [f.companyname, f.label, f.sampletext].some(x => String(x || "").toLowerCase().includes(q.toLowerCase())))
    .map(f => ({ ...f, confrank: f.confidence === "High" ? 3 : f.confidence === "Medium" ? 2 : 1,
                 totalin: Number(f.totalin) || 0, totalout: Number(f.totalout) || 0 }));
  const { sorted, sortKey, sortDir, toggle } = useSortable(rows, "", "desc");
  const H = ({ label, k, numeric }) =>
    <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;

  if (err) return <ErrorMsg msg={err} onRetry={() => load()} />;
  if (!data) return <Spinner />;

  const cnt = (s, c) => (data.counts || []).filter(x => x.status === s && (!c || x.confidence === c))
    .reduce((t, x) => t + x.n, 0);
  const selIds = Object.keys(sel).filter(k => sel[k]).map(Number);
  const allSel = sorted.length > 0 && sorted.every(f => sel[f.id]);
  const lr = data.lastRun;

  return <>
    {!embedded && <SectionHeader title="MCA Monitor" count={sorted.length} actions={[
      <SearchBar key="q" value={q} onChange={setQ} placeholder="Borrower, MCA or description…" />,
      <Btn key="x" onClick={() => exportCSV(sorted, ["companyname", "label", "kind", "confidence", "maxscore", "txncount",
          "firstdate", "lastdate", "totalin", "totalout", "status", "decidedby", "decidedat", "note", "sampletext"],
        "mca_findings.csv")}>Export</Btn>,
      isAdmin && <Btn key="full" disabled={running} onClick={() => runScan(true)}
           title="Rescan every transaction in the holding period and rebuild every finding nobody has decided yet. Use after editing the MCA list.">Full Rescan</Btn>,
      isAdmin && <Btn key="run" variant="primary" disabled={running} onClick={() => runScan(false)}>{running ? "Scanning…" : "Run Scan"}</Btn>,
    ].filter(Boolean)} />}

    {!embedded && <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
      <Tile label="Open · High" value={cnt("Open", "High")} tone={cnt("Open", "High") ? "#993C1D" : undefined}
            active={status === "Open"} onClick={() => setStatus("Open")} sub="name match" />
      <Tile label="Open · Medium" value={cnt("Open", "Medium")} tone={cnt("Open", "Medium") ? "#7A5200" : undefined}
            active={status === "Open"} onClick={() => setStatus("Open")} sub="partial name / daily debits" />
      <Tile label="Open · Low" value={cnt("Open", "Low")} active={status === "Open"} onClick={() => setStatus("Open")} sub="weekly debits" />
      <Tile label="Acknowledged" value={cnt("Acknowledged")} active={status === "Acknowledged"} onClick={() => setStatus("Acknowledged")} />
      <Tile label="Cured" value={cnt("Cured")} active={status === "Cured"} onClick={() => setStatus("Cured")} />
      <Tile label="False Positive" value={cnt("False Positive")} active={status === "False Positive"} onClick={() => setStatus("False Positive")} />
      <Tile label="Last Scan" value={lr ? fmtDate(lr.startedat) : "never"}
            sub={lr ? (lr.errormessage ? "failed: " + lr.errormessage.slice(0, 60)
                       : `${Number(lr.txnsscanned || 0).toLocaleString()} txns · ${lr.isfull ? "full" : "incremental"}`) : ""}
            tone={lr && lr.errormessage ? "#993C1D" : undefined} />
    </div>}

    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
      {STATUSES.map(s => (
        <span key={s} onClick={() => setStatus(s)}
              style={{ cursor: "pointer", fontSize: 12, padding: "4px 12px", borderRadius: 14,
                       fontWeight: status === s ? 700 : 400,
                       background: status === s ? B.cyan : "transparent", color: status === s ? "#fff" : B.cyanDark,
                       border: "1px solid " + (status === s ? B.cyan : B.cyanLight) }}>{s === "all" ? "All" : s}</span>
      ))}
      {runMsg && <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 10 }}>{runMsg}</span>}
      {selIds.length > 0 && <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{selIds.length} selected</span>
        <Btn onClick={() => setDecide({ ids: selIds, status: "Acknowledged" })}>Acknowledge</Btn>
        <Btn onClick={() => setDecide({ ids: selIds, status: "Cured" })}>Cured</Btn>
        <Btn onClick={() => setDecide({ ids: selIds, status: "False Positive" })}>False Positive</Btn>
        {status !== "Open" && <Btn onClick={() => setDecide({ ids: selIds, status: "Open" })}>Reopen</Btn>}
      </span>}
    </div>

    {/* Embedded on an account's Monitoring tab the table sits directly in that
        tab's card, like the UCC and tax tables — not in a card of its own. */}
    <TableBox style={embedded ? { overflowX: "auto" } : { padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ width: 30, padding: "8px 10px", background: "var(--color-background-secondary)" }}>
            <input type="checkbox" checked={allSel}
                   onChange={e => setSel(e.target.checked ? Object.fromEntries(sorted.map(f => [f.id, true])) : {})} />
          </th>
          {!embedded && <H label="Borrower" k="companyname" />}
          <H label="Finding" k="label" />
          <H label="Confidence" k="confrank" />
          <H label="Txns" k="txncount" numeric />
          <H label="First" k="firstdate" />
          <H label="Last" k="lastdate" />
          <H label="Money In" k="totalin" numeric />
          <H label="Money Out" k="totalout" numeric />
          <H label="Status" k="status" />
        </tr></thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={embedded ? 9 : 10} style={{ padding: 24, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
            {embedded && status === "all" ? "No MCA activity found for this borrower."
              : status === "Open" ? "Nothing open. Every flagged transaction has been reviewed." : "No findings with this status."}
          </td></tr>}
          {sorted.map(f => (
            <Fragment key={f.id}>
              <tr style={{ cursor: "pointer", background: open === f.id ? "#F3FBFE" : "" }} onClick={() => toggleOpen(f.id)}>
                <Td style={{ width: 30 }} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={!!sel[f.id]} onChange={e => setSel(s => ({ ...s, [f.id]: e.target.checked }))} />
                </Td>
                {!embedded && <Td style={{ fontWeight: 500 }}>
                  <span style={{ color: B.cyanDark }} onClick={e => { e.stopPropagation(); go("account", { accountid: f.accountid, companyname: f.companyname }); }}>
                    {f.companyname || f.accountid}</span>
                </Td>}
                <Td>
                  <div style={{ fontWeight: 500 }}>{f.kind === "NAME" ? f.mcaname : "Recurring debit"}
                    {f.kind === "NAME" && f.mcatype && f.mcatype !== "MCA" &&
                      <span style={{ marginLeft: 6, fontSize: 11, color: "var(--color-text-secondary)" }}>({f.mcatype})</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", maxWidth: 460, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                       title={f.sampletext || ""}>{f.sampletext}</div>
                </Td>
                <Td><Pill tone={CONF[f.confidence] || CONF.Low} title={`Score ${f.maxscore}`}>{f.confidence}</Pill></Td>
                <TdNum>{f.txncount}</TdNum>
                <Td>{fmtDate(f.firstdate)}</Td>
                <Td>{fmtDate(f.lastdate)}</Td>
                <TdNum style={{ color: f.totalin ? "#0F6E56" : undefined }}>{money(f.totalin)}</TdNum>
                <TdNum style={{ color: f.totalout ? "#993C1D" : undefined }}>{money(f.totalout)}</TdNum>
                <Td>
                  <Pill tone={STATUS_TONE[f.status] || STATUS_TONE.Open}>{f.status}</Pill>
                  {f.newsincedecision > 0 && <span style={{ marginLeft: 6 }}><Pill tone={CONF.High}>{f.newsincedecision} new</Pill></span>}
                  {f.reopencount > 0 && f.status === "Open" && <span style={{ marginLeft: 6, fontSize: 11, color: "#993C1D" }}>reopened</span>}
                </Td>
              </tr>
              {open === f.id && <tr><td colSpan={embedded ? 9 : 10} style={{ padding: "4px 16px 16px 46px", background: "#F3FBFE", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0", flexWrap: "wrap" }}>
                  {f.status !== "Acknowledged" && <Btn onClick={() => setDecide({ ids: [f.id], status: "Acknowledged", finding: f })}>Acknowledge</Btn>}
                  {f.status !== "Cured" && <Btn onClick={() => setDecide({ ids: [f.id], status: "Cured" })}>Cured</Btn>}
                  {f.status !== "False Positive" && <Btn onClick={() => setDecide({ ids: [f.id], status: "False Positive" })}>False Positive</Btn>}
                  {f.status !== "Open" && <Btn onClick={() => setDecide({ ids: [f.id], status: "Open" })}>Reopen</Btn>}
                  {f.decidedby && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                    {f.status} by {f.decidedby} · {when(f.decidedat)}</span>}
                </div>
                {f.note && <div style={{ fontSize: 12, margin: "0 0 8px", whiteSpace: "pre-wrap" }}><b>Note:</b> {f.note}</div>}
                {!txns[f.id] ? <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Loading transactions…</div> :
                  <div style={{ maxHeight: 340, overflowY: "auto", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 6, background: "#fff" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>
                        {["Date", "Description", "Amount", "Match", ""].map((h, i) => (
                          <th key={i} style={{ position: "sticky", top: 0, background: "#fff", fontSize: 11, fontWeight: 600, color: B.cyanDark,
                                               textAlign: i === 2 ? "right" : "left", padding: "6px 10px", borderBottom: "1.5px solid " + B.cyanLight }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>{txns[f.id].map(t => (
                        <tr key={t.transactionid}>
                          <Td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(t.txndate)}</Td>
                          <Td style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{t.name}</Td>
                          <TdNum style={{ fontSize: 12, color: Number(t.amount) < 0 ? "#993C1D" : "#0F6E56" }}>{fmt(t.amount)}</TdNum>
                          <Td style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{t.matchedon} · {t.score}</Td>
                          <Td>{t.isnew ? <Pill tone={CONF.High}>new</Pill> : null}</Td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>}
              </td></tr>}
            </Fragment>
          ))}
        </tbody>
      </table>
    </TableBox>
    {!embedded && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8 }}>
      Funded borrowers (Accrual / Non Accrual), transactions on or after first funding. Money In is a likely MCA funding deposit;
      Money Out is repayments. Click a row for its transactions.
    </div>}

    {decide && <DecisionModal decide={decide} onClose={() => setDecide(null)}
                              onSaved={() => { setDecide(null); setTxns({}); load(); }} />}
  </>;
};

// ── Learning an alias ──
// The part of a bank description that matched the funder, when it is not the
// funder's full name: "PARKSIDE FUND" for Parkside Funding Group. Words that
// equal or abbreviate a word of the name (3+ letters), or a long word that is
// the name run together, taken as the longest unbroken run. Empty when the
// description already carried the whole name — that matches without help.
const LEGAL = new Set(["llc", "inc", "ltd", "corp", "co", "lp", "llp", "pllc", "the", "and"]);
const normWords = s => String(s || "").toLowerCase().replace(/&/g, " ").replace(/[^a-z0-9]+/g, " ").trim().split(" ").filter(Boolean);
export const suggestAlias = (mcaname, text) => {
  const toks = normWords(mcaname).filter(t => !LEGAL.has(t));
  if (!toks.length) return "";
  const compact = toks.join("");
  const words = normWords(text);
  const hit = w => (w.length >= 3 && toks.some(t => t === w || t.startsWith(w))) || (w.length >= 8 && compact.includes(w));
  let best = [], cur = [];
  words.forEach(w => { if (hit(w)) { cur.push(w); if (cur.length > best.length) best = [...cur]; } else cur = []; });
  if (!best.length) return "";
  const full = best.length === toks.length && best.every((w, i) => w === toks[i]);
  const out = best.join(" ");
  return full || out.replace(/ /g, "").length < 4 ? "" : out.toUpperCase();
};

const DecisionModal = ({ decide, onClose, onSaved }) => {
  const { api } = useApp();
  const learnable = decide.status === "Acknowledged" && decide.finding && decide.finding.kind === "NAME";
  const suggested = learnable ? suggestAlias(decide.finding.mcaname, decide.finding.sampletext) : "";
  const [alias, setAlias] = useState(suggested);
  const [learn, setLearn] = useState(!!suggested);
  const [note, setNote] = useState("");
  const [all, setAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const needsNote = decide.status === "Cured" || decide.status === "False Positive";
  const help = {
    "Acknowledged":   "It is an MCA and we know about it. New activity keeps collecting on the finding and shows as \"new\".",
    "Cured":          "The MCA is paid off or stopped. Any new activity after today reopens the finding.",
    "False Positive": "Not an MCA. New activity on the same description is collected silently.",
    "Open":           "Put it back on the list.",
  }[decide.status];

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.post("saveMCAFinding", { ids: decide.ids, status: decide.status, note, allBorrowers: all });
      if (r && r.errors && r.errors.length) { setErr(r.errors.map(e => e.error).join("; ")); setSaving(false); return; }
      if (learnable && learn && alias.trim()) {
        try {
          await api.post("saveMCAAlias", { add: { mcacompanyid: decide.finding.mcacompanyid, alias, source: "learned",
                                                  note: "From finding " + decide.finding.id } });
        } catch (e) {
          setErr("Acknowledged, but the alias was not saved: " + e.message);
          setSaving(false); return;
        }
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <Modal title={`${decide.status === "Open" ? "Reopen" : decide.status} · ${decide.ids.length} finding${decide.ids.length > 1 ? "s" : ""}`} onClose={onClose}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 14 }}>{help}</div>
      <Field label="Note" required={needsNote}>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
                  placeholder={decide.status === "False Positive" ? "e.g. Prosperity Bank operating loan, not an MCA" : "What you found"}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999", fontFamily: "inherit" }} />
      </Field>
      {learnable && (
        <div style={{ border: "0.5px solid " + B.cyanLight, borderRadius: 6, padding: "10px 12px", marginBottom: 14, background: "#F3FBFE" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
            <input type="checkbox" checked={learn} onChange={e => setLearn(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Save as an alias for <b>{decide.finding.mcaname}</b> — any borrower's description containing it is flagged High from now on.</span>
          </label>
          <input value={alias} onChange={e => { setAlias(e.target.value); setLearn(!!e.target.value.trim()); }}
                 placeholder="The funder's name as it appears in the description"
                 style={{ width: "100%", boxSizing: "border-box", padding: "6px 10px", fontSize: 13, borderRadius: 4, border: "1px solid #999", fontFamily: "var(--font-mono)" }} />
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
            {suggested ? "Suggested from: " : "The description carried the full name, so no alias is needed. From: "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{decide.finding.sampletext}</span>
          </div>
        </div>
      )}
      {decide.status === "False Positive" && (
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} style={{ marginTop: 2 }} />
          <span>For all borrowers — stop flagging this description for anyone, and clear it where it is already open.</span>
        </label>
      )}
      {err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" disabled={saving || (needsNote && !note.trim())} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
      </div>
    </Modal>
  );
};
